import React from 'react';
import {
    List,
    Edit,
    Create,
    Show,
    SimpleForm,
    SimpleShowLayout,
    Datagrid,
    TextField,
    EmailField,
    DateField,
    TextInput,
    BooleanInput,
    PasswordInput,
    Filter,
    Pagination,
    Labeled,
    required,
    minLength,
    email,
    useRecordContext,
    useNotify,
    useRefresh,
    Button,
    TopToolbar,
    DeleteButton,
    EditButton,
    ShowButton
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { stripedDataGridSx } from './styles/dataGridStyles';

const UserFilter = (props) => (
    <Filter {...props}>
        <TextInput label="Search" source="q" alwaysOn />
        <BooleanInput label="Admin Users Only" source="is_admin" />
        <BooleanInput label="Active Users Only" source="active" />
    </Filter>
);

const UserPagination = props => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />;

// Custom field to display full name
const FullNameField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const fullName = [record.first_name, record.last_name].filter(Boolean).join(' ');
    return <span>{fullName || '-'}</span>;
};

// Custom field to display admin status as a badge
const AdminBadgeField = () => {
    const record = useRecordContext();
    if (!record) return null;
    return record.is_admin ? (
        <Chip label="Admin" color="error" size="small" />
    ) : (
        <Chip label="User" color="default" size="small" />
    );
};

// Custom field to display active status as a badge
const ActiveBadgeField = () => {
    const record = useRecordContext();
    if (!record) return null;
    return record.active ? (
        <Chip label="Active" color="success" size="small" />
    ) : (
        <Chip label="Inactive" color="warning" size="small" />
    );
};

// Password Reset Button (Admin only)
const PasswordResetButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const handlePasswordReset = async () => {
        const newPassword = window.prompt(
            `Enter new password for ${record.username}:`,
            ''
        );

        if (!newPassword) {
            notify('Password reset cancelled', { type: 'info' });
            return;
        }

        if (newPassword.length < 6) {
            notify('Password must be at least 6 characters', { type: 'error' });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const apiUrl = `${window.location.protocol}//${window.location.hostname}/api`;

            const response = await fetch(`${apiUrl}/users/${record.id}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify({ newPassword }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Password reset failed');
            }

            notify('Password reset successfully', { type: 'success' });
            refresh();
        } catch (error) {
            notify(`Error: ${error.message}`, { type: 'error' });
        }
    };

    return (
        <Button
            label="Reset Password"
            onClick={handlePasswordReset}
            startIcon={<LockResetIcon />}
        />
    );
};

// Custom actions for Edit view
const UserEditActions = () => (
    <TopToolbar>
        <ShowButton />
        <PasswordResetButton />
        <DeleteButton />
    </TopToolbar>
);

// Custom actions for Show view
const UserShowActions = () => (
    <TopToolbar>
        <EditButton />
        <PasswordResetButton />
        <DeleteButton />
    </TopToolbar>
);

export const UserList = props => {
    return (
        <List
            filters={<UserFilter />}
            sort={{ field: 'username', order: 'ASC' }}
            perPage={25}
            pagination={<UserPagination />}
            bulkActionButtons={false}
            {...props}
        >
            <Datagrid
                rowClick="edit"
                sx={stripedDataGridSx}
            >
                <TextField source="username" label="Username" />
                <FullNameField label="Name" />
                <EmailField source="email" label="Email" />
                <TextField source="phone" label="Phone" />
                <AdminBadgeField label="Role" />
                <ActiveBadgeField label="Status" />
                <DateField source="last_login" label="Last Login" showTime />
                <DateField source="created_at" label="Created" />
            </Datagrid>
        </List>
    );
};

export const UserEdit = props => (
    <Edit undoable={false} actions={<UserEditActions />} {...props}>
        <SimpleForm>
            <TextInput disabled source="id" />
            <TextInput
                source="username"
                disabled
                helperText="Username cannot be changed"
            />
            <TextInput source="first_name" label="First Name" />
            <TextInput source="last_name" label="Last Name" />
            <TextInput source="email" label="Email" type="email" validate={[email()]} />
            <TextInput source="phone" label="Phone" />
            <BooleanInput
                source="is_admin"
                label="Admin User"
                helperText="Admin users can manage other users"
            />
            <BooleanInput
                source="active"
                label="Active"
                helperText="Inactive users cannot log in"
            />
            <Box sx={{ marginTop: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <PasswordInput
                    source="password"
                    label="New Password (optional)"
                    helperText="Leave blank to keep current password. Minimum 6 characters."
                    validate={[minLength(6)]}
                />
            </Box>
        </SimpleForm>
    </Edit>
);

export const UserCreate = props => (
    <Create {...props}>
        <SimpleForm redirect="list">
            <TextInput
                source="username"
                label="Username"
                validate={[required()]}
                helperText="Required. Unique identifier for login."
            />
            <PasswordInput
                source="password"
                label="Password"
                validate={[required(), minLength(6)]}
                helperText="Required. Minimum 6 characters."
            />
            <TextInput source="first_name" label="First Name" />
            <TextInput source="last_name" label="Last Name" />
            <TextInput source="email" label="Email" type="email" validate={[email()]} />
            <TextInput source="phone" label="Phone" />
            <BooleanInput
                source="is_admin"
                label="Admin User"
                defaultValue={false}
                helperText="Admin users can manage other users"
            />
        </SimpleForm>
    </Create>
);

export const UserShow = props => (
    <Show actions={<UserShowActions />} {...props}>
        <SimpleShowLayout>
            <Labeled label="User ID">
                <TextField source="id" />
            </Labeled>
            <Labeled label="Username">
                <TextField source="username" />
            </Labeled>
            <Labeled label="First Name">
                <TextField source="first_name" />
            </Labeled>
            <Labeled label="Last Name">
                <TextField source="last_name" />
            </Labeled>
            <Labeled label="Email">
                <EmailField source="email" />
            </Labeled>
            <Labeled label="Phone">
                <TextField source="phone" />
            </Labeled>
            <Labeled label="Role">
                <AdminBadgeField />
            </Labeled>
            <Labeled label="Status">
                <ActiveBadgeField />
            </Labeled>
            <Labeled label="Last Login">
                <DateField source="last_login" showTime />
            </Labeled>
            <Labeled label="Created At">
                <DateField source="created_at" showTime />
            </Labeled>
            <Labeled label="Updated At">
                <DateField source="updated_at" showTime />
            </Labeled>
        </SimpleShowLayout>
    </Show>
);
